import TranslateIcon from "@mui/icons-material/Translate";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import i18n from "../../config/i18n";

declare global {
  interface Window {
    google: any;
    googleTranslateElementInit: () => void;
  }
}

interface GoogleTranslateProps {
  languages?: string;
  pageLanguage?: string;
  enableGoogleTranslate?: boolean;
}

const SCRIPT_ID = "google-translate-script";
const DEFAULT_LANGUAGES = "en,ru,uz,tg";
const DEFAULT_PAGE_LANGUAGE = "en";

const LANG_META: Record<
  string,
  { label: string; shortCode: string; flag: string }
> = {
  en: { label: "English", shortCode: "EN", flag: "/WebIcons/UsFlagSVG.svg" },
  ru: { label: "Russian", shortCode: "RU", flag: "/WebIcons/RuFlagSVG.svg" },
  uz: { label: "Uzbek", shortCode: "UZ", flag: "/WebIcons/UzFlagSVG.svg" },
  tg: { label: "Tajik", shortCode: "TJ", flag: "/WebIcons/TjFlagSVG.svg" },
};

const LANGUAGE_SHORT_CODES: Record<string, Record<string, string>> = {
  en: { en: "EN", ru: "RU", uz: "UZ", tg: "TJ" },
  ru: { en: "АНГ", ru: "РУС", uz: "УЗБ", tg: "ТАД" },
  uz: { en: "ING", ru: "RUS", uz: "OʻZ", tg: "TOJ" },
  tg: { en: "АНГ", ru: "РУС", uz: "ӮЗБ", tg: "ТҶ" },
};

const normalizeLanguage = (lang?: string | null) => {
  if (!lang) {
    return "";
  }
  return lang.split("-")[0].toLowerCase();
};

const parseLanguages = (languages: string) =>
  languages
    .split(",")
    .map((code) => normalizeLanguage(code.trim()))
    .filter(Boolean);

const getGoogleTranslateCookieLang = () => {
  if (typeof document === "undefined") {
    return "";
  }
  const match = document.cookie.match(/googtrans=\/[a-z]{2}\/([a-z]{2})/);
  return match?.[1] ?? "";
};

const getShortCode = (langCode: string, uiLanguage: string) => {
  const normalizedUiLanguage =
    normalizeLanguage(uiLanguage) || DEFAULT_PAGE_LANGUAGE;
  const localizedMap =
    LANGUAGE_SHORT_CODES[normalizedUiLanguage] || LANGUAGE_SHORT_CODES.en;
  return (
    localizedMap[langCode] ||
    LANG_META[langCode]?.shortCode ||
    langCode.toUpperCase()
  );
};

const GoogleTranslate: React.FC<GoogleTranslateProps> = ({
  languages = DEFAULT_LANGUAGES,
  pageLanguage = DEFAULT_PAGE_LANGUAGE,
  enableGoogleTranslate = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const pendingLangRef = useRef<string | null>(null);
  const initRef = useRef(false);
  const [uiLanguage, setUiLanguage] = useState(
    () =>
      normalizeLanguage(i18n.language) ||
      normalizeLanguage(localStorage.getItem("i18nextLng")) ||
      DEFAULT_PAGE_LANGUAGE
  );

  const available = useMemo(() => parseLanguages(languages), [languages]);
  const availableSet = useMemo(() => new Set(available), [available]);
  const normalizedPageLanguage =
    normalizeLanguage(pageLanguage) || DEFAULT_PAGE_LANGUAGE;

  const detectCurrentLanguage = useCallback(() => {
    if (typeof window === "undefined") {
      return normalizedPageLanguage;
    }

    const storedLang = normalizeLanguage(localStorage.getItem("i18nextLng"));
    if (storedLang && availableSet.has(storedLang)) {
      return storedLang;
    }

    const i18nLang = normalizeLanguage(i18n.language);
    if (i18nLang && availableSet.has(i18nLang)) {
      return i18nLang;
    }

    const cookieLang = normalizeLanguage(getGoogleTranslateCookieLang());
    if (cookieLang && availableSet.has(cookieLang)) {
      return cookieLang;
    }

    return available[0] || normalizedPageLanguage || DEFAULT_PAGE_LANGUAGE;
  }, [available, availableSet, normalizedPageLanguage]);

  const [selectedLang, setSelectedLang] = useState<string>(() =>
    detectCurrentLanguage()
  );

  const setSelectedLangSafe = useCallback((lang: string) => {
    setSelectedLang((prev) => (prev === lang ? prev : lang));
  }, []);

  const applyGoogleTranslate = useCallback((code: string) => {
    const select =
      document.querySelector<HTMLSelectElement>(".goog-te-combo");
    if (!select) {
      return false;
    }
    if (select.value !== code) {
      select.value = code;
      select.dispatchEvent(new Event("change"));
    }
    return true;
  }, []);

  const applyPendingLanguage = useCallback(() => {
    if (!pendingLangRef.current) {
      return;
    }
    if (applyGoogleTranslate(pendingLangRef.current)) {
      pendingLangRef.current = null;
    }
  }, [applyGoogleTranslate]);

  const initTranslateElement = useCallback(() => {
    if (initRef.current) {
      return;
    }
    if (!window.google?.translate?.TranslateElement) {
      return;
    }
    initRef.current = true;

    new window.google.translate.TranslateElement(
      {
        pageLanguage: normalizedPageLanguage,
        includedLanguages: available.join(","),
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      },
      "google_translate_element"
    );

    applyPendingLanguage();
    setSelectedLangSafe(detectCurrentLanguage());
  }, [
    available,
    normalizedPageLanguage,
    applyPendingLanguage,
    detectCurrentLanguage,
    setSelectedLangSafe,
  ]);

  useEffect(() => {
    if (!enableGoogleTranslate || typeof window === "undefined") {
      return;
    }

    window.googleTranslateElementInit = () => {
      initTranslateElement();
    };

    const existingScript = document.getElementById(SCRIPT_ID);
    if (!existingScript) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src =
        "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      document.body.appendChild(s);
    } else {
      initTranslateElement();
    }
  }, [enableGoogleTranslate, initTranslateElement]);

  useEffect(() => {
    if (!enableGoogleTranslate) {
      return;
    }

    const normalized = normalizeLanguage(selectedLang);
    if (!normalized) {
      return;
    }

    document.cookie = `googtrans=/${normalizedPageLanguage}/${normalized};path=/;`;
    if (!applyGoogleTranslate(normalized)) {
      pendingLangRef.current = normalized;
    }
  }, [
    enableGoogleTranslate,
    selectedLang,
    normalizedPageLanguage,
    applyGoogleTranslate,
  ]);

  useEffect(() => {
    setSelectedLangSafe(detectCurrentLanguage());
  }, [detectCurrentLanguage, setSelectedLangSafe]);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const normalized = normalizeLanguage(lng);
      if (normalized) {
        setUiLanguage(normalized);
      }
      if (normalized && availableSet.has(normalized)) {
        setSelectedLangSafe(normalized);
      }
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [availableSet, setSelectedLangSafe]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== "i18nextLng") {
        return;
      }
      const normalized = normalizeLanguage(event.newValue);
      if (normalized && availableSet.has(normalized)) {
        setSelectedLangSafe(normalized);
      }
      if (normalized) {
        setUiLanguage(normalized);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [availableSet, setSelectedLangSafe]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setSelectedLangSafe(detectCurrentLanguage());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [detectCurrentLanguage, setSelectedLangSafe]);

  const open = Boolean(anchorEl);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const applyLanguage = useCallback(
    (code: string) => {
      const normalized = normalizeLanguage(code);
      if (!normalized || !availableSet.has(normalized)) {
        handleClose();
        return;
      }

      void i18n.changeLanguage(normalized);
      localStorage.setItem("i18nextLng", normalized);
      setSelectedLangSafe(normalized);
      handleClose();

      if (!enableGoogleTranslate) {
        return;
      }

      document.cookie = `googtrans=/${normalizedPageLanguage}/${normalized};path=/;`;
      if (!applyGoogleTranslate(normalized)) {
        pendingLangRef.current = normalized;
      }
      window.location.reload();
    },
    [
      availableSet,
      applyGoogleTranslate,
      handleClose,
      enableGoogleTranslate,
      normalizedPageLanguage,
      setSelectedLangSafe,
    ]
  );

  return (
    <Box
      className="notranslate"
      translate="no"
      data-gt-ignore="true"
      sx={{ display: "inline-flex", alignItems: "center" }}
    >
      {/* Hidden target for the Google widget (script needs this in DOM) */}
      <Box id="google_translate_element" sx={{ display: "none" }} />

      {/* Custom UI matching navbar profile style */}
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpen}
        startIcon={
          LANG_META[selectedLang]?.flag ? (
            <Box
              component="img"
              src={LANG_META[selectedLang].flag}
              alt={`${LANG_META[selectedLang].label} flag`}
              sx={{
                width: 25,
                height: 20,
                display: "block",
                borderRadius: 0.5,
              }}
            />
          ) : (
            <TranslateIcon />
          )
        }
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        sx={{
          textTransform: "none",
          borderColor: "#e5e7eb",
          color: "#374151",
          backgroundColor: "white",
          "&:hover": { backgroundColor: "#f3f4f6", borderColor: "#9ca3af" },
        }}
      >
        {getShortCode(selectedLang, uiLanguage)}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} keepMounted>
        {available
          .filter((code) => code !== selectedLang)
          .map((code) => (
            <MenuItem key={code} onClick={() => applyLanguage(code)}>
              <ListItemIcon sx={{ minWidth: 35 }}>
                {LANG_META[code]?.flag ? (
                  <Box
                    component="img"
                    src={LANG_META[code].flag}
                    alt={`${LANG_META[code].label} flag`}
                    sx={{
                      width: 25,
                      height: 20,
                      display: "block",
                      borderRadius: 0.5,
                    }}
                  />
                ) : null}
              </ListItemIcon>
              <ListItemText>
                {getShortCode(code, uiLanguage)}
              </ListItemText>
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};

export default GoogleTranslate;
